/**
 * Admin Tests Page
 *
 * Page wrapper for the Phase 2 Test Panel.
 * Only accessible to admin users.
 */

import { Link } from 'react-router-dom'
import { ArrowLeft, FlaskConical } from 'lucide-react'
import { Phase2TestPanel } from '@/components/admin/Phase2TestPanel'
import { Phase3TestPanel } from '@/components/admin/Phase3TestPanel'
import { AdminOnly } from '@/components/AdminOnly'

export default function AdminTestsPage() {
  return (
    <AdminOnly
      fallback={
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-slate-400 mb-4">This page is only available to administrators.</p>
            <Link to="/" className="text-blue-400 hover:text-blue-300">
              Return to Dashboard
            </Link>
          </div>
        </div>
      }
    >
      <div className="min-h-screen bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Link>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <FlaskConical className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Test Suite</h1>
                <p className="text-slate-400">Phase 2 & Phase 3 Integration Testing</p>
              </div>
            </div>
          </div>

          {/* Phase 3 Test Panel */}
          <Phase3TestPanel />

          {/* Phase 2 Test Panel */}
          <div className="mt-8">
            <Phase2TestPanel />
          </div>

          {/* Info Section */}
          <div className="mt-8 bg-slate-800/50 rounded-lg border border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">About Integration Tests</h2>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-slate-300">
              <div>
                <h3 className="font-medium text-white mb-2">Phase 3: Game Loop</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>All 8 action types submit and resolve correctly</li>
                  <li>Tick processor runs full pipeline without errors</li>
                  <li>16-tick electoral cycle completes with valid election</li>
                  <li>Election results are mathematically correct</li>
                  <li>Edge cases handled gracefully</li>
                  <li>Tick processing under 30 second target</li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-white mb-2">Phase 2: Data Integrity</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>Cohort system: generation, distribution, validation</li>
                  <li>Party system: creation, district states, loyalties</li>
                  <li>Candidate system: data, constraints, recruitment</li>
                  <li>Visualizations: ideology comparison, 2D projection</li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-white mb-2">Action Types Tested</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>Campaign - voter persuasion in districts</li>
                  <li>Fundraise - raise money from donor blocs</li>
                  <li>Organize - build district infrastructure</li>
                  <li>Attack - negative campaigns against rivals</li>
                  <li>PR - messaging and damage control</li>
                  <li>Internal - manage candidate loyalty</li>
                  <li>Poll - instant polling snapshot</li>
                  <li>Recruit - instant candidate recruitment</li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-white mb-2">Election Verification</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>Vote counts positive and consistent</li>
                  <li>Vote shares sum to ~100% per district</li>
                  <li>D'Hondt seat allocation with deterministic tiebreaker</li>
                  <li>Government formation (majority/coalition/minority)</li>
                  <li>Next election scheduled automatically</li>
                  <li>Cohort loyalties updated post-election</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminOnly>
  )
}
